import {
  TEntityResponse,
  TEntityListResponse,
  TCreateGatewayReqBody,
  TGatewayResBody,
  TGatewaySummaryResBody,
  TUpdateGatewayReqBody,
  TGatewayFrameSummaryResBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TGatewaysClient = {
  create(body: TCreateGatewayReqBody): Promise<TEntityResponse<'gateway', TGatewayResBody> | undefined>;
  update(id: string, body: TUpdateGatewayReqBody): Promise<TEntityResponse<'gateway', TGatewayResBody> | undefined>;
  delete(id: string): Promise<void>;
  get(id: string): Promise<TEntityResponse<'gateway', TGatewayResBody> | undefined>;
  get_frames_for_gateway(
    limit: number,
    page: number,
    gatewayId: string,
    sort: 'asc' | 'desc'
  ): Promise<TEntityListResponse<'gateway-frame', TGatewayFrameSummaryResBody> | undefined>;
  list(
    areaId: string,
    limit: number,
    page: number
  ): Promise<TEntityListResponse<'gateway', TGatewaySummaryResBody> | undefined>;
  list_all_gateways(
    limit: number,
    page: number
  ): Promise<TEntityListResponse<'gateway', TGatewaySummaryResBody> | undefined>;
  list_company_gateways(
    filter: string,
    limit: number,
    page: number
  ): Promise<TEntityListResponse<'gateway', TGatewaySummaryResBody> | undefined>;
};

function GatewaysClient(apiClient: TAPIClient): TGatewaysClient {
  return {
    async create(body: TCreateGatewayReqBody): Promise<TEntityResponse<'gateway', TGatewayResBody> | undefined> {
      const data = await apiClient.post<TCreateGatewayReqBody, TEntityResponse<'gateway', TGatewayResBody>>(
        `/gateways`,
        body
      );

      if (data && data.type !== 'gateway') {
        throw new Error('Invalid entity type received, expected: gateway, received: ' + data.type);
      }

      return data;
    },

    async update(
      id: string,
      body: TUpdateGatewayReqBody
    ): Promise<TEntityResponse<'gateway', TGatewayResBody> | undefined> {
      const data = await apiClient.put<TUpdateGatewayReqBody, TEntityResponse<'gateway', TGatewayResBody>>(
        `/gateways/${id}`,
        body
      );

      if (data && data.type !== 'gateway') {
        throw new Error('Invalid entity type received, expected: gateway, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/gateways/${id}`);
    },

    async get(id: string): Promise<TEntityResponse<'gateway', TGatewayResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'gateway', TGatewayResBody>>(`/gateways/${id}`);

      if (data && data.type !== 'gateway') {
        throw new Error('Invalid entity type received, expected: gateway, received: ' + data.type);
      }

      return data;
    },

    async get_frames_for_gateway(
      limit: number,
      page: number,
      gatewayId: string,
      sort: 'asc' | 'desc'
    ): Promise<TEntityListResponse<'gateway-frame', TGatewayFrameSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'gateway-frame', TGatewayFrameSummaryResBody>>(
        `/gateways/${gatewayId}/frames?sort[created_at]=${sort}&limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'gateway-frame') {
        throw new Error('Invalid entity type received, expected: gateway-frame, received: ' + data.type);
      }

      return data;
    },

    async list(
      areaId: string,
      limit: number,
      page: number
    ): Promise<TEntityListResponse<'gateway', TGatewaySummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'gateway', TGatewaySummaryResBody>>(
        `/areas/${areaId}/gateways?limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'gateway') {
        throw new Error('Invalid entity type received, expected: gateway, received: ' + data.type);
      }

      return data;
    },

    async list_all_gateways(
      limit: number,
      page: number
    ): Promise<TEntityListResponse<'gateway', TGatewaySummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'gateway', TGatewaySummaryResBody>>(
        `/gateways?limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'gateway') {
        throw new Error('Invalid entity type received, expected: gateway, received: ' + data.type);
      }

      return data;
    },

    async list_company_gateways(
      filter: string,
      limit: number,
      page: number
    ): Promise<TEntityListResponse<'gateway', TGatewaySummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'gateway', TGatewaySummaryResBody>>(
        `/gateways?${filter}&limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'gateway') {
        throw new Error('Invalid entity type received, expected: gateway, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TGatewaysClient, GatewaysClient };
